import type { StaffBlockWire } from "@loot-os/functions/src/dashboard-api/trpc/routers/liveBreakWire.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { endUserLabel, staffEndUserLabel } from "../lib/liveBreakStaff";
import { useTRPC } from "../lib/trpc";
import { ShopperPickerDialog } from "./ShopperPickerDialog";

/** Blocks a shopper in one set (`liveBreaks.blocks.block`, admins). */
export function BlockShopperDialog({
  hostEndUserId,
  setName,
  blockedIds,
}: {
  hostEndUserId: string | null;
  setName: string;
  blockedIds: ReadonlySet<string>;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const block = useMutation(
    trpc.liveBreaks.blocks.block.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.users.liveBreaks.pathKey() });
      },
    }),
  );
  return (
    <ShopperPickerDialog
      triggerLabel="Block a shopper"
      title={
        hostEndUserId === null
          ? "Block from house live breaks"
          : `Block from ${setName}'s live breaks`
      }
      description="Their queued entries there are released and their holds given back. They can no longer buy, chat or watch until unblocked."
      actionLabel="Block"
      pending={block.isPending}
      pickedLabel={(user) =>
        user.id === hostEndUserId ? "The host" : blockedIds.has(user.id) ? "Blocked" : undefined
      }
      onPick={(user) =>
        block.mutateAsync({ hostEndUserId, endUserId: user.id }).then((result) => {
          const released = result.releasedEntryIds.length;
          toast.success(
            `Blocked ${endUserLabel(user)}${released > 0 ? `; ${released} queued ${released === 1 ? "entry" : "entries"} released` : ""}.`,
          );
        })
      }
    />
  );
}

/** Lifts a Block after a confirmation (`liveBreaks.blocks.unblock`, admins). */
export function UnblockButton({ block }: { block: StaffBlockWire }) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const name = staffEndUserLabel(block.endUser);
  const where = block.host ? `${staffEndUserLabel(block.host)}'s live breaks` : "house live breaks";
  const unblock = useMutation(
    trpc.liveBreaks.blocks.unblock.mutationOptions({
      onSuccess: () => {
        toast.success(`${name} can join ${where} again.`);
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: trpc.liveBreaks.pathKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.users.liveBreaks.pathKey() });
      },
    }),
  );
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger render={<Button type="button" variant="ghost" size="sm" />}>
        Unblock
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{`Unblock ${name}?`}</AlertDialogTitle>
          <AlertDialogDescription>
            {`They can buy, chat and watch in ${where} again. Entries the Block released stay released.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={unblock.isPending}
            onClick={() =>
              unblock.mutate({
                hostEndUserId: block.host?.endUserId ?? null,
                endUserId: block.endUser.endUserId,
              })
            }
          >
            {unblock.isPending ? "Unblocking…" : "Unblock"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
